/**
 * IXVerify v2 — the continuous capture surface (ixv2 WP-3.2; plan §3.2/§4).
 * Reworked 2026-08-21 for pilot feedback round 1 (mobile ergonomics).
 *
 * One getUserMedia viewfinder, two modes, order-free:
 *   - DOCUMENT mode: 4:3 full-sheet framing with corner guides. The preview
 *     letterboxes; the FULL SENSOR FRAME is what gets saved — the guide is an
 *     aid, never a mask (R12). Quality floor: a negotiated stream below
 *     2,000px on the long edge falls back to the native camera for document
 *     shots automatically (per-device capability probed empirically from the
 *     live track). The R12 completeness check is ADVISORY: a retake/add-page
 *     prompt offers a recorded "use anyway" override alongside the fix path.
 *     Captured pages render as a thumbnail strip with per-page remove/retake.
 *     The SERVER decides what a removal means (pilot r1.1, owner decision
 *     2026-08-21): pre-signature pages hard-delete for real; signed-over
 *     pages soft-discard (bytes retained). The sheet copy states both.
 *   - KIT mode: the same viewfinder. The primary flow is PHOTOGRAPHING the
 *     kit container — whole bottle/box — so the label is readable; live
 *     barcode detection stays on as an automatic bonus when a barcode is in
 *     frame (zxing-wasm, no shutter tap needed).
 *
 * The in-app viewfinder exists to kill iOS's per-shot "Use Photo / Retake"
 * confirmation (3–4 s/photo the 30-second clock can't afford — R11) and to
 * enable live barcode reads. The native `<input capture>` path remains the
 * automatic fallback (permission denied / stream failure / below the quality
 * floor), and BOTH modes also offer a photo-library picker (same accept list,
 * no `capture` attribute — pilot feedback #5), keeping the HEIC→JPEG
 * accept-list trick and the EXIF fix (v1 port, applied to renditions only —
 * originals upload untouched, R12).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Barcode,
  Camera,
  CameraOff,
  CheckCircle2,
  FileText,
  Image as ImageIcon,
  ImagePlus,
  Keyboard,
  Loader2,
  Package,
  RefreshCw,
  ScanLine,
  AlertTriangle,
  ArrowRight,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import type { UploadTask } from '../../lib/uploadQueue';
import type { CapturePage, CapturePrompt, KitProgress } from '../../lib/captureMachine';
import {
  cameraSupported,
  captureFrameFromVideo,
  DOCUMENT_STREAM_CONSTRAINTS,
  probeStreamCapability,
  stopStream,
  type CapturedFrame,
} from '../../lib/image';
import { startBarcodeScanLoop, type BarcodeScannerHandle } from '../../lib/barcode';

export type CaptureMode = 'document' | 'label';

export interface CaptureSurfaceProps {
  mode: CaptureMode;
  onModeChange: (mode: CaptureMode) => void;
  /** Captured document pages so far (chip strip + page numbering). */
  uploadTasks: UploadTask[];
  onRetryUpload: (taskId: string) => void;
  /** The merged page strip (server assets + local uploads — pilot r1). */
  pages: CapturePage[];
  /** Local preview object URLs keyed by pageIndex (fresh captures only). */
  pagePreviews: Record<number, string>;
  /** Discard a page (remove or retake — both discard; retake keeps shooting).
   *  The server decides hard-vs-soft from signature coverage (pilot r1.1). */
  onDiscardPage: (page: CapturePage, intent: 'remove' | 'retake') => void;
  /** The page key currently being discarded (spinner), or null. */
  discardBusyKey: string | null;
  kitProgress: KitProgress;
  /** Last kit scan feedback tick, e.g. "KIT-1002 ✓ matched". */
  lastKitTick: string | null;
  /** Server completeness verdict driving the retake/add-page loop (R12). */
  prompt: CapturePrompt | null;
  onDismissPrompt: () => void;
  /** Record a use-anyway override of the capture advisory (pilot r1). */
  onOverrideCapture: () => void;
  overridingCapture: boolean;
  extracting: boolean;
  readingLabel: boolean;
  /** A full-sensor frame captured from the in-app viewfinder. */
  onDocumentFrame: (frame: CapturedFrame) => void;
  /** A file from the native-camera / library-picker fallback (jpeg/png/pdf). */
  onDocumentFile: (file: File) => void;
  onPasteText: () => void;
  onBarcodeDetected: (payload: string) => void;
  onLabelFrame: (frame: CapturedFrame) => void;
  onLabelFile: (file: File) => void;
  onManualKit: () => void;
  /** Proceed to the composite review. */
  onDone: () => void;
  canFinish: boolean;
}

type StreamState = 'starting' | 'live' | 'fallback';

export default function CaptureSurface(props: CaptureSurfaceProps) {
  const {
    mode, onModeChange, uploadTasks, onRetryUpload,
    pages, pagePreviews, onDiscardPage, discardBusyKey,
    kitProgress, lastKitTick,
    prompt, onDismissPrompt, onOverrideCapture, overridingCapture,
    extracting, readingLabel,
    onDocumentFrame, onDocumentFile, onPasteText, onBarcodeDetected,
    onLabelFrame, onLabelFile, onManualKit, onDone, canFinish,
  } = props;

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scannerRef = useRef<BarcodeScannerHandle | null>(null);
  const docCameraInputRef = useRef<HTMLInputElement>(null);
  const docLibraryInputRef = useRef<HTMLInputElement>(null);
  const labelCameraInputRef = useRef<HTMLInputElement>(null);
  const labelLibraryInputRef = useRef<HTMLInputElement>(null);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [streamState, setStreamState] = useState<StreamState>('starting');
  const [fallbackReason, setFallbackReason] = useState<string | null>(null);
  const [shutterBusy, setShutterBusy] = useState(false);
  const [shutterFlash, setShutterFlash] = useState(false);
  const [scannerLive, setScannerLive] = useState(false);
  const [pageSheet, setPageSheet] = useState<CapturePage | null>(null);

  // -- stream lifecycle -----------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    async function start() {
      if (!cameraSupported()) {
        setStreamState('fallback');
        setFallbackReason('camera_unsupported');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia(DOCUMENT_STREAM_CONSTRAINTS);
        if (cancelled) {
          stopStream(stream);
          return;
        }
        streamRef.current = stream;
        // Per-device stream capability, verified empirically on the LIVE track
        // (§4): below the floor → native camera for document shots.
        const capability = probeStreamCapability(stream);
        if (!capability.meetsQualityFloor) {
          setFallbackReason(`stream_below_quality_floor:${capability.longEdge}px`);
          setStreamState('fallback');
          stopStream(stream);
          streamRef.current = null;
          return;
        }
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          try {
            await video.play();
          } catch {
            /* iOS wants a gesture sometimes; the playsInline+muted combo usually avoids it */
          }
        }
        setStreamState('live');
      } catch {
        if (!cancelled) {
          setFallbackReason('permission_or_stream_failure');
          setStreamState('fallback');
        }
      }
    }
    void start();
    return () => {
      cancelled = true;
      scannerRef.current?.stop();
      scannerRef.current = null;
      stopStream(streamRef.current);
      streamRef.current = null;
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    };
  }, []);

  // -- live barcode loop (kit mode only — the automatic bonus) --------------
  useEffect(() => {
    scannerRef.current?.stop();
    scannerRef.current = null;
    setScannerLive(false);
    if (mode !== 'label' || streamState !== 'live') return undefined;
    const video = videoRef.current;
    if (!video) return undefined;
    const handle = startBarcodeScanLoop(video, onBarcodeDetected);
    scannerRef.current = handle;
    const poll = setInterval(() => setScannerLive(handle.isActive()), 400);
    return () => {
      clearInterval(poll);
      handle.stop();
    };
  }, [mode, streamState, onBarcodeDetected]);

  // -- shutter (pilot r1 #4: explicit capturing feedback) -------------------
  const handleShutter = useCallback(async () => {
    const video = videoRef.current;
    if (!video || shutterBusy) return;
    setShutterBusy(true);
    // Camera-app flash: a brief white blink confirms the shot was taken.
    setShutterFlash(true);
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => setShutterFlash(false), 180);
    try {
      const frame = await captureFrameFromVideo(video);
      if (mode === 'document') onDocumentFrame(frame);
      else onLabelFrame(frame);
    } catch {
      // Frame grab failed — the native path still works.
      setFallbackReason('frame_grab_failed');
      setStreamState('fallback');
    } finally {
      setShutterBusy(false);
    }
  }, [mode, shutterBusy, onDocumentFrame, onLabelFrame]);

  const handleDocFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) onDocumentFile(file);
      e.target.value = '';
    },
    [onDocumentFile]
  );
  const handleLabelFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) onLabelFile(file);
      e.target.value = '';
    },
    [onLabelFile]
  );

  const uploadsPending = uploadTasks.filter((t) => t.status === 'uploading' || t.status === 'queued').length;
  const uploadErrors = uploadTasks.filter((t) => t.status === 'error');
  const newestPageKey = pages.length > 0 ? pages[pages.length - 1].key : null;

  const openCamera = () => (mode === 'document' ? docCameraInputRef.current?.click() : labelCameraInputRef.current?.click());
  const openLibrary = () => (mode === 'document' ? docLibraryInputRef.current?.click() : labelLibraryInputRef.current?.click());

  const pageLabel = (p: CapturePage) => (p.pageIndex != null ? `Page ${p.pageIndex + 1}` : p.kind === 'text' ? 'Pasted text' : 'Document');

  return (
    <div className="relative flex min-h-0 flex-1 flex-col bg-gray-950" data-testid="capture-surface">
      {/* Viewfinder / fallback area */}
      <div className="relative min-h-0 flex-1 overflow-hidden">
        {streamState !== 'fallback' ? (
          <>
            {/* The preview letterboxes (object-contain); the SAVED frame is the
                full sensor frame — the guide below is an aid, never a mask (R12). */}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="absolute inset-0 h-full w-full object-contain"
              data-testid="viewfinder"
            />
            {streamState === 'starting' && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/70">
                <Loader2 className="h-8 w-8 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                <p className="text-sm">Starting camera…</p>
              </div>
            )}
            {/* Corner guides */}
            {streamState === 'live' && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center" aria-hidden="true">
                <div className={mode === 'document' ? 'relative aspect-[3/4] h-[84%] max-w-[92%]' : 'relative aspect-[4/3] w-[80%] max-w-md'}>
                  {(['top-0 left-0 border-t-2 border-l-2 rounded-tl-lg', 'top-0 right-0 border-t-2 border-r-2 rounded-tr-lg', 'bottom-0 left-0 border-b-2 border-l-2 rounded-bl-lg', 'bottom-0 right-0 border-b-2 border-r-2 rounded-br-lg']).map((cls) => (
                    <span key={cls} className={`absolute h-7 w-7 border-white/80 ${cls}`} />
                  ))}
                  {mode === 'label' && scannerLive && (
                    <div className="absolute inset-x-4 top-1/2 -translate-y-1/2">
                      <div className="h-0.5 w-full rounded bg-primary-500/70" />
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Shutter flash — the "picture taken" blink (pilot r1 #4). */}
            {shutterFlash && (
              <div className="pointer-events-none absolute inset-0 bg-white/80 motion-reduce:bg-white/40" data-testid="shutter-flash" aria-hidden="true" />
            )}
            {/* Mode hint (pilot r1 #3: kits are PHOTOGRAPHED — whole container;
                barcodes are the automatic bonus, not the framing). */}
            {streamState === 'live' && (
              <div className="absolute inset-x-0 top-2 flex justify-center px-4">
                <span className="rounded-full bg-black/50 px-3 py-1 text-center text-xs font-medium text-white/90">
                  {mode === 'document'
                    ? 'Fit the whole sheet inside the guides — nothing cut off'
                    : scannerLive
                      ? 'Photograph the kit — whole bottle or box is fine. Barcodes scan by themselves.'
                      : 'Photograph the whole kit — bottle or box — so the label is readable, then tap the shutter'}
                </span>
              </div>
            )}
          </>
        ) : (
          /* Native-camera fallback (permission denied / stream failure / below
             the quality floor). Keeps capture fully working, and the photo
             library stays available in BOTH modes (pilot r1 #5). */
          <div className="flex h-full flex-col items-center justify-center gap-4 px-6 text-center" data-testid="native-fallback">
            <CameraOff className="h-10 w-10 text-white/40" aria-hidden="true" />
            <p className="max-w-xs text-sm text-white/70">
              {fallbackReason?.startsWith('stream_below_quality_floor')
                ? 'This device streams below document quality — the native camera will be used for sharp, complete captures.'
                : 'The in-app camera is unavailable. Use the native camera or pick a photo instead.'}
            </p>
            {/* Cross-device step A: on a desktop the natural input is the IXRS
                PDF itself — the wizard root accepts a drop. Shown for every
                fallback reason: a laptop webcam usually trips the quality floor. */}
            {mode === 'document' && (
              <p className="max-w-xs text-xs text-white/60" data-testid="drop-hint">
                On a computer, drag the IXRS PDF anywhere onto this screen. Kits can then be photographed on your phone — the
                same dispense stays in sync on both.
              </p>
            )}
            <div className="flex w-full max-w-xs flex-col gap-2">
              {mode === 'document' ? (
                <>
                  <button
                    type="button"
                    onClick={() => docCameraInputRef.current?.click()}
                    className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 motion-reduce:transition-none"
                  >
                    <Camera className="h-4 w-4" aria-hidden="true" /> Photograph the assignment
                  </button>
                  <button
                    type="button"
                    onClick={() => docLibraryInputRef.current?.click()}
                    className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg border border-white/30 px-4 py-2 text-sm font-medium text-white/90 transition-colors duration-150 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-primary-500 motion-reduce:transition-none"
                  >
                    <ImagePlus className="h-4 w-4" aria-hidden="true" /> Choose from library
                  </button>
                  <button
                    type="button"
                    onClick={onPasteText}
                    className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg border border-white/30 px-4 py-2 text-sm font-medium text-white/90 transition-colors duration-150 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-primary-500 motion-reduce:transition-none"
                  >
                    <FileText className="h-4 w-4" aria-hidden="true" /> Paste assignment text
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => labelCameraInputRef.current?.click()}
                    className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 motion-reduce:transition-none"
                  >
                    <Camera className="h-4 w-4" aria-hidden="true" /> Photograph the kit
                  </button>
                  <button
                    type="button"
                    onClick={() => labelLibraryInputRef.current?.click()}
                    className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg border border-white/30 px-4 py-2 text-sm font-medium text-white/90 transition-colors duration-150 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-primary-500 motion-reduce:transition-none"
                  >
                    <ImagePlus className="h-4 w-4" aria-hidden="true" /> Choose from library
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* R12 retake / add-page prompt — ADVISORY (pilot r1 #1): the fix path
            stays primary, but "use anyway" is an explicit, RECORDED option. */}
        {prompt && (
          <div className="absolute inset-x-3 bottom-3 rounded-xl border border-amber-500/60 bg-amber-950/90 p-3 text-amber-100" role="alert" data-testid="capture-prompt">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-400" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">
                  {prompt.kind === 'retake' ? 'This photo may be cut off' : 'More pages expected'}
                </p>
                <p className="mt-0.5 text-xs text-amber-200/90">
                  {prompt.kind === 'retake'
                    ? 'The completeness check could not see the whole sheet. Retake with all four corners inside the guides — or use this photo anyway (your decision is recorded).'
                    : prompt.pageLabelSeen
                      ? `The document says "${prompt.pageLabelSeen}" — add the missing page(s), or continue if this is everything (recorded).`
                      : 'The document appears to continue — add the next page, or continue if this is everything (recorded).'}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={onDismissPrompt}
                    className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-1.5 text-sm font-medium text-amber-950 transition-colors duration-150 hover:bg-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-300 motion-reduce:transition-none sm:min-h-0"
                  >
                    {prompt.kind === 'retake' ? (
                      <><RefreshCw className="h-4 w-4" aria-hidden="true" /> Retake now</>
                    ) : (
                      <><Plus className="h-4 w-4" aria-hidden="true" /> Add page</>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={onOverrideCapture}
                    disabled={overridingCapture}
                    data-testid="capture-override"
                    className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-amber-400/70 px-3 py-1.5 text-sm font-medium text-amber-100 transition-colors duration-150 hover:bg-amber-400/10 focus:outline-none focus:ring-2 focus:ring-amber-300 disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none sm:min-h-0"
                  >
                    {overridingCapture ? (
                      <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                    )}
                    {prompt.kind === 'retake' ? 'Use this photo anyway' : 'No more pages — continue'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Kit tick feedback */}
        {lastKitTick && !prompt && (
          <div className="absolute inset-x-0 bottom-3 flex justify-center" aria-live="polite">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-green-600 px-3 py-1.5 text-sm font-medium text-white">
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> {lastKitTick}
            </span>
          </div>
        )}
      </div>

      {/* Captured-page strip (document mode — pilot r1 #2): tap a page for
          remove/retake. The thumbnail popping in IS the capture confirmation. */}
      {mode === 'document' && pages.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto border-t border-gray-800 bg-gray-950 px-3 py-2" data-testid="page-strip">
          {pages.map((p) => {
            const preview = p.pageIndex != null ? pagePreviews[p.pageIndex] : undefined;
            const busy = discardBusyKey === p.key;
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => setPageSheet(p)}
                aria-label={`${pageLabel(p)} — options`}
                data-testid={`page-thumb-${p.key}`}
                className={`relative h-16 w-12 flex-shrink-0 overflow-hidden rounded-lg border transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 motion-reduce:transition-none ${
                  p.error ? 'border-red-500' : p.key === newestPageKey && p.uploading ? 'border-primary-500 ring-1 ring-primary-500' : 'border-gray-700 hover:border-gray-500'
                }`}
              >
                {preview ? (
                  <img src={preview} alt="" className="h-full w-full object-cover" />
                ) : (
                  <span className="flex h-full w-full items-center justify-center bg-gray-800 text-gray-400">
                    {p.kind === 'photo' ? <ImageIcon className="h-5 w-5" aria-hidden="true" /> : <FileText className="h-5 w-5" aria-hidden="true" />}
                  </span>
                )}
                {(p.uploading || busy) && (
                  <span className="absolute inset-0 flex items-center justify-center bg-black/50">
                    <Loader2 className="h-4 w-4 animate-spin text-white motion-reduce:animate-none" aria-hidden="true" />
                  </span>
                )}
                <span className="absolute inset-x-0 bottom-0 bg-black/60 px-0.5 text-center text-[10px] font-medium leading-4 text-white/90">
                  {p.pageIndex != null ? p.pageIndex + 1 : p.kind === 'text' ? 'txt' : 'doc'}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Status strip: upload chips + extraction state (quiet — never a blocking spinner, R11) */}
      <div className="flex flex-wrap items-center gap-1.5 border-t border-gray-800 bg-gray-950 px-3 py-1.5" aria-live="polite">
        {mode !== 'document' && pages.length > 0 && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-0.5 text-xs font-medium text-white/80">
            <FileText className="h-3.5 w-3.5" aria-hidden="true" /> {pages.length} page{pages.length === 1 ? '' : 's'}
          </span>
        )}
        {uploadsPending > 0 && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-0.5 text-xs font-medium text-white/80">
            <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" /> uploading {uploadsPending}
          </span>
        )}
        {uploadErrors.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => onRetryUpload(t.id)}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full bg-red-600/90 px-2.5 text-xs font-medium text-white transition-colors duration-150 hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-400 motion-reduce:transition-none sm:min-h-0 sm:py-0.5"
          >
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" /> retry {t.label}
          </button>
        ))}
        {extracting && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-600/30 px-2.5 py-0.5 text-xs font-medium text-primary-200">
            <ScanLine className="h-3.5 w-3.5" aria-hidden="true" /> reading assignment…
          </span>
        )}
        {readingLabel && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-600/30 px-2.5 py-0.5 text-xs font-medium text-primary-200">
            <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" /> reading label…
          </span>
        )}
        {kitProgress.assigned > 0 && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-0.5 text-xs font-medium tabular-nums text-white/80">
            <Barcode className="h-3.5 w-3.5" aria-hidden="true" /> kits {kitProgress.resolved}/{kitProgress.assigned}
          </span>
        )}
      </div>

      {/* Bottom controls (pilot r1 #4 + pilot r5): TWO rows, camera-app style.
          Row 1 — mode toggle (left) and the Review button (right).
          Row 2 — three-column grid that keeps the shutter DEAD CENTER, with
          only 44 px icon buttons beside it.
          Why two rows: the old single row (toggle · shutter · library ·
          keyboard · Review) needed ~414–466 px, and every iPhone is 375–430 px
          wide, so the grid overflowed to the right and the Review button was
          the thing that got clipped ("the blue button at the bottom right is
          cut off" — r4's 100dvh pin only fixed the VERTICAL cutoff). The widest
          row here is ~250 px. */}
      <div
        className="flex flex-shrink-0 flex-col gap-2 border-t border-gray-800 bg-gray-950 px-3 pt-2"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.625rem)' }}
      >
        <div className="flex items-center justify-between gap-3">
        {/* Mode toggle */}
        <div className="inline-flex items-center gap-0.5 rounded-lg border border-gray-700 bg-gray-900 p-0.5" role="tablist" aria-label="Capture mode">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'document'}
            onClick={() => onModeChange('document')}
            className={`inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-1.5 rounded-md px-2.5 text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 motion-reduce:transition-none sm:min-h-0 sm:min-w-0 sm:py-1 ${
              mode === 'document' ? 'bg-gray-700 text-primary-300' : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            <FileText className="h-4 w-4" aria-hidden="true" /> Doc
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'label'}
            onClick={() => onModeChange('label')}
            className={`inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-1.5 rounded-md px-2.5 text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 motion-reduce:transition-none sm:min-h-0 sm:min-w-0 sm:py-1 ${
              mode === 'label' ? 'bg-gray-700 text-primary-300' : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            <Package className="h-4 w-4" aria-hidden="true" /> Kits
          </button>
        </div>

        {/* Review — always fully on screen, never competing with the shutter row */}
        <button
          type="button"
          onClick={onDone}
          disabled={!canFinish}
          className="inline-flex min-h-[44px] flex-shrink-0 items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-gray-950 disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none"
          data-testid="capture-done"
        >
          Review <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </button>
        </div>

        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
        {/* Left of the shutter: photo-library picker */}
        <div className="flex items-center justify-self-start">
          <button
            type="button"
            aria-label="Choose from photo library"
            onClick={openLibrary}
            data-testid="library-button"
            className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-gray-700 text-gray-300 transition-colors duration-150 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-primary-500 motion-reduce:transition-none sm:h-9 sm:w-9"
          >
            <ImagePlus className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {/* Shutter — the one unmistakable control (camera-app scale: 80px,
            centered, pressed/busy state + flash + thumb pop-in as feedback). */}
        {streamState === 'live' ? (
          <button
            type="button"
            aria-label={mode === 'document' ? 'Capture document page' : 'Capture kit photo'}
            onClick={() => void handleShutter()}
            disabled={shutterBusy}
            className="inline-flex h-20 w-20 items-center justify-center justify-self-center rounded-full border-4 border-white/90 bg-white/15 transition-colors duration-150 hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-gray-950 active:bg-white/30 motion-reduce:transition-none"
            data-testid="shutter"
          >
            <span
              className={`rounded-full bg-white transition-all duration-150 motion-reduce:transition-none ${
                shutterBusy ? 'h-10 w-10 opacity-60' : 'h-[3.75rem] w-[3.75rem]'
              }`}
            />
          </button>
        ) : (
          <button
            type="button"
            aria-label="Open camera"
            onClick={openCamera}
            className="inline-flex h-20 w-20 items-center justify-center justify-self-center rounded-full border-4 border-white/60 bg-white/10 text-white transition-colors duration-150 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-gray-950 motion-reduce:transition-none"
          >
            <Camera className="h-8 w-8" aria-hidden="true" />
          </button>
        )}

        {/* Right of the shutter: mode-specific secondary (manual kit entry) */}
        <div className="flex items-center justify-self-end">
          {mode === 'label' && (
            <button
              type="button"
              aria-label="Type a kit number"
              onClick={onManualKit}
              className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-gray-700 text-gray-300 transition-colors duration-150 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-primary-500 motion-reduce:transition-none sm:h-9 sm:w-9"
            >
              <Keyboard className="h-5 w-5" aria-hidden="true" />
            </button>
          )}
        </div>
        </div>
      </div>

      {/* Per-page options sheet (pilot r1 #2): big tap targets, clear intent. */}
      {pageSheet && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-4 backdrop-blur-sm sm:items-center"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}
          role="dialog"
          aria-modal="true"
          aria-label={`${pageLabel(pageSheet)} options`}
          data-testid="page-sheet"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setPageSheet(null);
          }}
        >
          <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl dark:bg-gray-800">
            <div className="flex items-start justify-between gap-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{pageLabel(pageSheet)}</h3>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setPageSheet(null)}
                className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-gray-500 transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-700 sm:h-8 sm:w-8"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            {pageSheet.pageIndex != null && pagePreviews[pageSheet.pageIndex] ? (
              <img
                src={pagePreviews[pageSheet.pageIndex]}
                alt={`${pageLabel(pageSheet)} preview`}
                className="mx-auto mt-3 max-h-48 rounded-lg border border-gray-200 object-contain dark:border-gray-700"
              />
            ) : (
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {pageSheet.kind === 'text' ? 'The pasted assignment text.' : 'A captured page of the assignment document.'}
              </p>
            )}
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Before a signature is applied, removing a page deletes the photo for good. After a page has been
              signed over, it stays in the sealed evidence record and is only removed from this verification.
              Either way, the removal is recorded.
            </p>
            <div className="mt-4 flex flex-col gap-2">
              <button
                type="button"
                disabled={!pageSheet.discardAssetId}
                onClick={() => {
                  onDiscardPage(pageSheet, 'retake');
                  setPageSheet(null);
                }}
                data-testid="page-retake"
                className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none"
              >
                <RefreshCw className="h-4 w-4" aria-hidden="true" /> Retake this page
              </button>
              <button
                type="button"
                disabled={!pageSheet.discardAssetId}
                onClick={() => {
                  onDiscardPage(pageSheet, 'remove');
                  setPageSheet(null);
                }}
                data-testid="page-remove"
                className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none"
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" /> Remove page
              </button>
              {!pageSheet.discardAssetId && (
                <p className="text-center text-xs text-gray-500 dark:text-gray-400">Still uploading — try again in a moment.</p>
              )}
              <button
                type="button"
                onClick={() => setPageSheet(null)}
                className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 motion-reduce:transition-none"
              >
                Keep this page
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden native inputs — accept list keeps iOS transcoding HEIC → JPEG
          (the deliberate house trick); PDFs welcome on the document path.
          TWO inputs per mode (pilot r1 #5): the camera input keeps
          capture="environment"; the library input has NO capture attribute so
          the OS opens the photo picker. */}
      <input
        ref={docCameraInputRef}
        type="file"
        accept="image/jpeg,image/png,application/pdf"
        capture="environment"
        onChange={handleDocFile}
        className="hidden"
        data-testid="doc-file-input"
      />
      <input
        ref={docLibraryInputRef}
        type="file"
        accept="image/jpeg,image/png,application/pdf"
        onChange={handleDocFile}
        className="hidden"
        data-testid="doc-library-input"
      />
      <input
        ref={labelCameraInputRef}
        type="file"
        accept="image/jpeg,image/png"
        capture="environment"
        onChange={handleLabelFile}
        className="hidden"
        data-testid="label-file-input"
      />
      <input
        ref={labelLibraryInputRef}
        type="file"
        accept="image/jpeg,image/png"
        onChange={handleLabelFile}
        className="hidden"
        data-testid="label-library-input"
      />
    </div>
  );
}
